import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  TextArea,
  Button,
  Toast,
  NavBar,
  Selector,
  SearchBar,
  List,
  ImageUploader,
  Dialog,
  SafeArea,
  Space,
  Tag,
} from 'antd-mobile';
import {
  LeftOutline,
  CameraOutline,
  DeleteOutline,
} from 'antd-mobile-icons';
import { ticketApi } from '@/api/tickets';
import { apiClient } from '@/api/client';
import { usersApi } from '@/api/users';
import { validateImage, compressImage } from '@/utils/validators';
import { useAuthStore } from '@/stores/authStore';
import type { CreateTicketRequest } from '@shared/types';

interface UserOption {
  id: number;
  real_name: string;
  username: string;
  role: string;
}

export default function TicketCreatePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [responderOptions, setResponderOptions] = useState<UserOption[]>([]);
  const [showResponderList, setShowResponderList] = useState(false);
  const [selectedResponder, setSelectedResponder] = useState<UserOption | null>(null);
  const [imageFiles, setImageFiles] = useState<any[]>([]);
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  // 搜索责任人（调用后端）
  const searchResponders = useCallback(async (keyword: string) => {
    setSearching(true);
    try {
      const kw = keyword.trim();
      let data: UserOption[] = [];
      if (kw) {
        const res = await usersApi.search(kw, 'responder');
        data = (res.data.data || []) as UserOption[];
      } else {
        const res = await usersApi.list({ role: 'responder' });
        data = (res.data.data || []) as UserOption[];
      }
      setResponderOptions(data);
      setShowResponderList(data.length > 0);
    } catch (_e) {
      setResponderOptions([]);
      setShowResponderList(false);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearchChange = (val: string) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      searchResponders(val);
    }, 300);
  };

  const handleSelectResponder = (responder: UserOption) => {
    setSelectedResponder(responder);
    form.setFieldsValue({ responder_id: responder.id });
    setShowResponderList(false);
    setResponderOptions([]);
  };

  // 图片上传处理
  const handleImageUpload = async (file: File): Promise<any> => {
    const validation = validateImage(file);
    if (!validation.valid) {
      Toast.show({ icon: 'fail', content: validation.message });
      return null;
    }

    Toast.show({ icon: 'loading', content: '压缩中…', duration: 0 });
    let compressedBlob: Blob;
    try {
      compressedBlob = await compressImage(file, { maxWidth: 1080, quality: 0.8 });
    } catch {
      Toast.clear();
      Toast.show({ icon: 'fail', content: '图片压缩失败' });
      return null;
    }

    Toast.show({ icon: 'loading', content: '上传中…', duration: 0 });
    try {
      const resp = await apiClient.post('/upload/image', compressedBlob, {
        headers: { 'Content-Type': file.type },
      });
      const { url } = resp.data.data;

      Toast.clear();
      return { url: url || '' };
    } catch (err) {
      Toast.clear();
      Toast.show({ icon: 'fail', content: '图片上传失败，请重试' });
      return null;
    }
  };

  // 提交表单
  const handleSubmit = async (values: any) => {
    if (!selectedResponder) {
      Toast.show({ icon: 'fail', content: '请选择责任人' });
      return;
    }

    setLoading(true);
    try {
      const imageKeys = imageFiles
        .map((f) => f.url)
        .filter(Boolean) as string[];

      const payload: CreateTicketRequest = {
        level: values.level,
        responder_id: selectedResponder.id,
        description: values.description,
        image_keys: imageKeys.length > 0 ? imageKeys : undefined,
      };

      const res = await ticketApi.create(payload);
      const { id, ticket_no } = res.data.data!;

      Dialog.alert({
        title: '提交成功',
        content: (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>?</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
              工单已创建
            </div>
            <div
              style={{
                fontSize: 14,
                color: '#6B7280',
                background: '#F5F7FA',
                padding: '8px 12px',
                borderRadius: 8,
                fontFamily: 'monospace',
              }}
            >
              {ticket_no}
            </div>
            <div style={{ fontSize: 13, color: '#9CA3AF', marginTop: 8 }}>
              提交人：{user?.real_name} · {new Date().toLocaleString('zh-CN')}
            </div>
          </div>
        ),
        confirmText: '查看详情',
        onConfirm: () => {
          navigate(`/tickets/${id}`);
        },
      });
    } catch (err: any) {
      const msg = err.response?.data?.message || '提交失败，请重试';
      Toast.show({ icon: 'fail', content: msg });
    } finally {
      setLoading(false);
    }
  };

  const levelOptions = [
    { label: '🔴 红警 — 严重问题，需立即处理', value: 'red' },
    { label: '🟡 黄警 — 一般问题，需尽快处理', value: 'yellow' },
  ];

  return (
    <div className="ticket-create-page">
      <SafeArea position="top" />

      {/* 顶部导航 */}
      <NavBar
        onBack={() => navigate(-1)}
        className="create-nav"
        backArrow={<LeftOutline fontSize={20} />}
      >
        上报问题
      </NavBar>

      {/* 表单 */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="create-form"
        initialValues={{ level: 'yellow' }}
        footer={
          <div className="form-footer">
            <Button
              block
              type="submit"
              size="large"
              loading={loading}
              className="submit-btn"
            >
              {loading ? '提交中…' : '提交工单'}
            </Button>
          </div>
        }
      >
        {/* 严重等级 */}
        <Form.Item
          name="level"
          label="严重等级"
          rules={[{ required: true, message: '请选择严重等级' }]}
          className="form-item"
        >
          <Selector
            options={levelOptions}
            className="level-selector"
          />
        </Form.Item>

        {/* 责任人 */}
        <Form.Item
          name="responder_id"
          label="责任人"
          rules={[{ required: true, message: '请选择责任人' }]}
          className="form-item"
        >
          <div className="responder-section">
            {selectedResponder ? (
              <div
                className="responder-selected"
                onClick={() => {
                  setSelectedResponder(null);
                  form.setFieldsValue({ responder_id: undefined });
                }}
              >
                <div className="responder-info">
                  <div className="responder-avatar">
                    {selectedResponder.real_name.charAt(0)}
                  </div>
                  <div className="responder-meta">
                    <div className="responder-name">
                      {selectedResponder.real_name}
                    </div>
                    <div className="responder-username">
                      @{selectedResponder.username}
                    </div>
                  </div>
                </div>
                <DeleteOutline
                  fontSize={18}
                  color="#ff4d4f"
                  className="responder-delete"
                />
              </div>
            ) : (
              <div className="responder-search">
                <SearchBar
                  placeholder="搜索责任人姓名"
                  onChange={handleSearchChange}
                  className="responder-search-bar"
                />
                {showResponderList && (
                  <div className="responder-dropdown">
                    {searching ? (
                      <div className="responder-loading">搜索中…</div>
                    ) : responderOptions.length === 0 ? (
                      <div className="responder-empty">未找到匹配的责任人</div>
                    ) : (
                      <List className="responder-list">
                        {responderOptions.map((responder) => (
                          <List.Item
                            key={responder.id}
                            onClick={() => handleSelectResponder(responder)}
                            className="responder-option"
                            prefix={
                              <div className="responder-option-avatar">
                                {responder.real_name.charAt(0)}
                              </div>
                            }
                          >
                            <div className="responder-option-name">
                              {responder.real_name}
                            </div>
                            <div className="responder-option-username">
                              @{responder.username}
                            </div>
                          </List.Item>
                        ))}
                      </List>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </Form.Item>

        {/* 问题描述 */}
        <Form.Item
          name="description"
          label="问题描述"
          rules={[
            { required: true, message: '请描述问题' },
            { max: 2000, message: '描述不能超过2000字' },
          ]}
          className="form-item"
        >
          <TextArea
            placeholder="请详细描述发现的问题，包括：
? 问题位置
? 具体情况
? 可能的影响"
            rows={5}
            maxLength={2000}
            showCount
            className="desc-textarea"
          />
        </Form.Item>

        {/* 图片上传 */}
        <Form.Item
          label={
            <div className="upload-label">
              <span>现场照片</span>
              <span className="upload-hint">（最多2张，每张≤2MB）</span>
            </div>
          }
          className="form-item"
        >
          <ImageUploader
            value={imageFiles}
            onChange={setImageFiles}
            maxCount={2}
            upload={handleImageUpload}
            beforeUpload={(file) => {
              const validation = validateImage(file);
              if (!validation.valid) {
                Toast.show({ icon: 'fail', content: validation.message });
                return null;
              }
              return file;
            }}
            onDelete={() => {
              // 允许删除
              return true;
            }}
            className="image-uploader"
          >
            <div className="upload-trigger">
              <CameraOutline fontSize={28} color="#2E5BFF" />
              <span className="upload-trigger-text">添加照片</span>
            </div>
          </ImageUploader>
        </Form.Item>
      </Form>

      <style>{`
        .ticket-create-page {
          min-height: 100vh;
          background: #F5F7FA;
          display: flex;
          flex-direction: column;
        }

        .create-nav {
          background: white;
          border-bottom: 1px solid #F0F2F5;
        }

        .create-nav .adm-nav-bar-title {
          font-size: 17px;
          font-weight: 600;
        }

        .create-form {
          flex: 1;
          padding: 16px;
        }

        .create-form .adm-list-body {
          background: transparent;
        }

        .form-item {
          margin-bottom: 20px;
        }

        .form-item .adm-form-item-label {
          font-size: 15px;
          font-weight: 600;
          color: #1F2433;
          margin-bottom: 10px;
        }

        .level-selector .adm-selector-item {
          border-radius: 10px;
          padding: 12px 16px;
          font-size: 14px;
        }

        .level-selector .adm-selector-item-active {
          background: #2E5BFF;
          color: white;
          border-color: #2E5BFF;
        }

        .responder-section {
          position: relative;
        }

        .responder-selected {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: white;
          border-radius: 10px;
          border: 1px solid #E8ECF3;
          cursor: pointer;
        }

        .responder-selected:active {
          background: #F5F7FA;
        }

        .responder-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .responder-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2E5BFF 0%, #6B8DFF 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 600;
        }

        .responder-meta {
          display: flex;
          flex-direction: column;
        }

        .responder-name {
          font-size: 15px;
          font-weight: 600;
          color: #1F2433;
        }

        .responder-username {
          font-size: 12px;
          color: #9CA3AF;
        }

        .responder-delete {
          padding: 4px;
        }

        .responder-search {
          position: relative;
        }

        .responder-search-bar {
          --adm-color-primary: #2E5BFF;
        }

        .responder-search-bar .adm-search-bar-input-box {
          background: white;
          border: 1px solid #E8ECF3;
          border-radius: 10px;
        }

        .responder-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          background: white;
          border-radius: 10px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          z-index: 100;
          max-height: 240px;
          overflow-y: auto;
          border: 1px solid #F0F2F5;
        }

        .responder-loading,
        .responder-empty {
          padding: 16px;
          text-align: center;
          font-size: 14px;
          color: #9CA3AF;
        }

        .responder-list {
          --padding-left: 0;
          --padding-right: 0;
        }

        .responder-list .adm-list-body {
          border-radius: 10px;
        }

        .responder-option {
          padding: 12px 16px !important;
        }

        .responder-option .adm-list-item-content {
          padding: 0 !important;
        }

        .responder-option-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #F0F2F5;
          color: #6B7280;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          margin-right: 12px;
        }

        .responder-option-name {
          font-size: 14px;
          font-weight: 600;
          color: #1F2433;
        }

        .responder-option-username {
          font-size: 12px;
          color: #9CA3AF;
        }

        .desc-textarea {
          border-radius: 10px;
          font-size: 15px;
          line-height: 1.7;
        }

        .desc-textarea .adm-text-area-element {
          padding: 12px;
        }

        .upload-label {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .upload-hint {
          font-size: 12px;
          color: #9CA3AF;
          font-weight: 400;
        }

        .image-uploader .adm-image-uploader-upload-button {
          width: 100px;
          height: 100px;
          border-radius: 10px;
          border: 1px dashed #d0d0d0;
          background: white;
        }

        .upload-trigger {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 6px;
          width: 100%;
          height: 100%;
        }

        .upload-trigger-text {
          font-size: 12px;
          color: #9CA3AF;
        }

        .form-footer {
          padding: 16px 0;
        }

        .submit-btn {
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          height: 48px;
        }

        .submit-btn.adm-button-primary {
          background: linear-gradient(135deg, #2E5BFF 0%, #6B8DFF 100%);
          border: none;
        }

        .submit-btn.adm-button-primary:active {
          opacity: 0.9;
        }
      `}</style>
    </div>
  );
}